/**
 * @fileoverview PDF Field Mapping component that allows users to configure field positions on PDF documents
 */

import React, { useRef, useState, useEffect } from "react";
import { Card, Button, Space, Modal, message } from "antd";
import { FormOutlined, UploadOutlined } from "@ant-design/icons";
import PDFViewer from "./PDFViewer";
import FieldMarker from "./FieldMarkers";
import ToolbarActions from "./ToolbarActions";
import ConfigPreviewModal from "./ConfigPreviewModal";
import FieldConfigurationForm from "./FieldConfigurationForm";
import {
  calculateDisplayPosition,
  calculatePDFCoordinates,
  generateConfig,
} from "./utils";
import _ from "lodash";
import { useIntl } from "react-intl";

/**
 * Converts a base64 string to a Blob object
 * @param {string} base64 - The base64 string to convert
 * @returns {Blob} - The resulting PDF Blob
 */
const base64ToBlob = (base64) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: "application/pdf" });
};

/**
 * Component for handling PDF and configuration file uploads
 * @param {Object} props
 * @param {Function} props.onPDFUpload - Handler for PDF file upload
 * @param {Function} props.onConfigUpload - Handler for configuration file upload
 * @param {Object} props.intl - Internationalization object
 */
const FileUploadSection = ({ onPDFUpload, onConfigUpload, intl }) => (
  <Space direction="vertical" style={{ width: "100%" }}>
    <input type="file" accept="application/pdf" onChange={onPDFUpload} />
    <Space>
      <Button icon={<UploadOutlined />}>
        <label style={{ cursor: "pointer" }}>
          {intl.formatMessage({ id: "app.settings.forms.load_configiration" })}
          <input
            type="file"
            accept="application/json"
            onChange={onConfigUpload}
            style={{ display: "none" }}
          />
        </label>
      </Button>
    </Space>
  </Space>
);

/**
 * Modal component for configuring field properties
 * @param {Object} props
 * @param {boolean} props.visible - Controls modal visibility
 * @param {Function} props.onCancel - Handler for modal cancellation
 * @param {Function} props.onFinish - Handler for form submission
 * @param {Object} props.intl - Internationalization object
 */
const FieldConfigurationModal = ({ visible, onCancel, onFinish, intl }) => (
  <Modal
    title={intl.formatMessage({ id: "app.settings.forms.field_configiration" })}
    open={visible}
    onCancel={onCancel}
    footer={null}
  >
    <FieldConfigurationForm onFinish={onFinish} intl={intl} />
  </Modal>
);

/**
 * Main PDF Field Mapper component
 * @component
 * @param {Object} props
 * @param {Function} props.onSave - Callback function when configuration is saved
 * @param {string} props.buttonText - Text to display on the trigger button
 * @param {Object} props.initialConfig - Initial field configuration
 * @param {string} props.base64Content - Base64 encoded PDF content
 * @param {boolean} props.standalone - Whether to render as a modal (true) or inline (false)
 *
 * @description
 * PDFFieldMapper allows users to:
 * - Upload and view PDF documents
 * - Place and configure form fields on the PDF
 * - Add marker fields with multiple positions
 * - Drag and reposition fields
 * - Save and load field configurations
 * - Export configurations as JSON
 *
 * The component supports two rendering modes:
 * 1. Standalone mode (with modal)
 * 2. Inline mode (without modal)
 */
const PDFFieldMapper = ({
  onSave = () => {},
  buttonText = "Configure PDF Mapping",
  initialConfig = null,
  base64Content = null,
  standalone = false, // new prop to determine if component should render its own modal
  isFormContext = { isFormContext },
}) => {
  const canvasRef = useRef(null);
  const intl = useIntl();
  const [visible, setVisible] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [fields, setFields] = useState([]);
  const [isAddingField, setIsAddingField] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [tempField, setTempField] = useState(null);
  const [draggedField, setDraggedField] = useState(null);
  const [markerMapping, setMarkerMapping] = useState({});
  const [isPlacingMarkers, setIsPlacingMarkers] = useState(false);
  const [markerValues, setMarkerValues] = useState([]);
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const [isPdfReady, setIsPdfReady] = useState(false);
  const pendingConfigRef = useRef(null);
  const [showMarkers, setShowMarkers] = useState(false);
  const hasLoadedConfig = useRef(false);
  const [currentArrayIndex, setCurrentArrayIndex] = useState(0);

  useEffect(() => {
    if (base64Content) {
      const blob = base64ToBlob(base64Content);
      const fileUrl = URL.createObjectURL(blob);
      setPdfFile(fileUrl);
      setVisible(true); // Auto-show content when base64Content is provided
    }
  }, [base64Content]);

  useEffect(() => {
    if (initialConfig) {
      if (isPdfReady) {
        loadConfiguration(initialConfig);
      } else {
        pendingConfigRef.current = initialConfig;
      }
    }
  }, [initialConfig, isPdfReady]);

  useEffect(() => {
    if (isPdfReady) {
      const timer = setTimeout(() => {
        const canvas = document.querySelector(".react-pdf__Page canvas");
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            setShowMarkers(true); // Only show markers after delay
            setFields((current) => [...current]);
          }
        }
      }, 500);

      return () => {
        clearTimeout(timer);
        setShowMarkers(false);
      };
    }
  }, [isPdfReady]);

  const handleFileChange = (event) => {
    if (!base64Content) {
      // Only allow file upload if base64Content is not provided
      const file = event.target.files[0];
      if (file && file.type === "application/pdf") {
        const fileUrl = URL.createObjectURL(file);
        setPdfFile(fileUrl);
      }
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleDocumentLoadError = (error) => {
    console.error("PDF load error:", error);
    message.error(
      intl.formatMessage({ id: "app.settings.forms.pdf_load_failed" })
    );
  };

  const handlePageLoadSuccess = (page) => {
    const canvas = document.querySelector(".react-pdf__Page canvas");
    if (canvas) {
      const newDimensions = {
        width: canvas.width,
        height: canvas.height,
      };
      setPdfDimensions(newDimensions);
      canvasRef.current = canvas;
      setIsPdfReady(true);

      if (pendingConfigRef.current) {
        setTimeout(() => {
          loadConfiguration(pendingConfigRef.current);
          pendingConfigRef.current = null;
        }, 100);
      }
    }
  };

  const startAddingField = () => {
    if (isPlacingMarkers) {
      message.info("Please finish placing all markers first");
      return;
    }
    setIsAddingField(true);
    message.info(intl.formatMessage({ id: "app.settings.forms.place_field" }));
  };

  /**
   * Handles the placement of fields on the PDF
   * @param {Event} event - Mouse event
   */
  const handleFieldPlacement = (event) => {
    if (!isAddingField) return;

    const canvas = document.querySelector(".react-pdf__Page canvas");
    if (!canvas) return;

    const coords = calculatePDFCoordinates(event, canvas);

    if (isPlacingMarkers && selectedField) {
      if (selectedField.type === "list") {
        setMarkerMapping((prev) => {
          // Use field.id as the unique identifier
          const mappingKey = `${selectedField.id}_${selectedField.name}`;
          const existingMapping = prev[mappingKey] || {};
          const existingCoords =
            existingMapping[selectedField.subFieldName] || [];

          return {
            ...prev,
            [mappingKey]: {
              [selectedField.subFieldName]: [
                ...(Array.isArray(existingCoords) ? existingCoords : []),
                coords,
              ],
            },
          };
        });
      } else if (selectedField.type === "checkbox") {
        const currentValue = markerValues[currentMarkerIndex];
        setMarkerMapping((prev) => ({
          ...prev,
          [selectedField.name]: {
            ...prev[selectedField.name],
            [currentValue]: coords,
          },
        }));
      }

      // Rest of the marker placement logic...
      if (currentMarkerIndex < markerValues.length - 1) {
        // ...existing increment and message logic...
        setCurrentMarkerIndex((prev) => prev + 1);

        // Different messages for array and checkbox types
        if (selectedField.type === "list") {
          message.info(
            intl.formatMessage(
              { id: "app.settings.forms.placing_list_marker" },
              {
                current: currentMarkerIndex + 2,
                total: selectedField.arraySize,
                field: markerValues[currentMarkerIndex + 1],
              }
            )
          );
        } else {
          message.info(
            `${intl.formatMessage({
              id: "app.settings.forms.place_marker_for_value",
            })} ${markerValues[currentMarkerIndex + 1]}`
          );
        }
      } else {
        // ...existing completion logic...
        setIsAddingField(false);
        setIsPlacingMarkers(false);
        setCurrentMarkerIndex(0);
        setMarkerValues([]);
        message.success(
          intl.formatMessage({
            id:
              selectedField.type === "list"
                ? "app.settings.forms.place_markers_placed_successfully"
                : "app.settings.forms.place_markers_placed_successfully",
          })
        );
      }
    } else {
      // ...existing non-marker field placement logic...
      setTempField({
        x: coords.x,
        y: coords.y,
        page: currentPage - 1,
      });
      setIsModalVisible(true);
    }
  };

  const handleFieldNameSubmit = (values) => {
    const {
      name,
      entity,
      type,
      format,
      spacing,
      markerValues: newMarkerValues,
      value_type,
      value,
      arraySize,
      subFieldName,
    } = values;

    if (
      (type === "checkbox" || type === "list") &&
      newMarkerValues?.length > 0
    ) {
      setMarkerValues(newMarkerValues);
      setCurrentMarkerIndex(0);
      setIsPlacingMarkers(true);
      message.info(
        `${intl.formatMessage({
          id: "app.settings.forms.place_marker_for_value",
        })} ${newMarkerValues[0]}`
      );
    }

    if (tempField) {
      const newField = {
        ...tempField,
        id: Date.now(),
        type,
        ...(value_type !== "const" && {
          name,
          entity,
        }),
        ...(value_type === "const" && {
          value,
        }),
        ...(type === "checkbox" && { checkbox_config: {} }),
        ...(type === "list" && {
          arraySize,
          subFieldName,
        }),
        ...(format && { format }),
        ...(value_type && { value_type }),
        ...(spacing && { spacing: parseInt(spacing) }),
      };

      setFields([...fields, newField]);
      setSelectedField(newField);
    }

    setIsModalVisible(false);
    setTempField(null);
  };

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < numPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  /**
   * Loads a field configuration
   * @param {Object} config - The configuration object to load
   */
  const loadConfiguration = (config) => {
    try {
      if (hasLoadedConfig.current) return;

      const processedFields = (config.fields || config).map((field) => {
        const processedField = {
          ...field,
          id: Date.now() + Math.random(),
          type: field.type,
          x: field.x,
          y: field.y,
          name: field.field || field.name,
          entity: field.entity,
          value_type: field.value_type,
          page: field.page || 0,
        };

        if (field.list_config) {
          processedField.type = "list";
          processedField.subFieldName =
            Object.keys(field.list_config.mapping)[0] || "name";
          processedField.arraySize =
            field.list_config.mapping[processedField.subFieldName]?.length || 0;
        }

        return processedField;
      });

      setFields(processedFields);

      const newMarkerMapping = {};
      processedFields.forEach((field) => {
        if (field.checkbox_config) {
          newMarkerMapping[field.name] = field.checkbox_config.mapping;
        }
        if (field.list_config && field.list_config.mapping) {
          const subFieldName = Object.keys(field.list_config.mapping)[0];
          if (
            subFieldName &&
            Array.isArray(field.list_config.mapping[subFieldName])
          ) {
            // Use field.id as the unique identifier
            const mappingKey = `${field.id}_${field.name}`;
            newMarkerMapping[mappingKey] = {
              [subFieldName]: field.list_config.mapping[subFieldName],
            };
          }
        }
      });

      setMarkerMapping(newMarkerMapping);

      // Force re-render after a small delay
      setTimeout(() => {
        setFields((current) => [...current]);
      }, 50);

      hasLoadedConfig.current = true;
      message.success(
        intl.formatMessage({ id: "app.settings.forms.configuration_loaded" })
      );
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: "app.settings.forms.configuration_load_failed",
        })
      );
      console.error("Load error:", error);
    }
  };

  // Reset the load flag when component unmounts or when initialConfig changes
  useEffect(() => {
    hasLoadedConfig.current = false;
    return () => {
      hasLoadedConfig.current = false;
    };
  }, [initialConfig]);

  const handleConfigFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const config = JSON.parse(e.target.result);
          loadConfiguration(config);
        } catch (error) {
          message.error(
            intl.formatMessage({
              id: "app.settings.forms.invalid_configuration",
            })
          );
          console.error("Parse error:", error);
        }
      };
      reader.readAsText(file);
    }
  };

  /**
   * Saves and downloads the current configuration
   * @param {Object} data - The configuration data to save
   */
  const saveConfiguration = () => {
    if (typeof onSave !== "function") {
      message.error(
        intl.formatMessage({ id: "app.settings.forms.save_handler_missing" })
      );
      return;
    }

    const config = generateConfig(fields, markerMapping);

    try {
      onSave(config);
      message.success(
        intl.formatMessage({
          id: "app.settings.forms.field_configuration_saved",
        })
      );
      setVisible(false);
    } catch (error) {
      message.error(
        intl.formatMessage({ id: "app.settings.forms.save_failed" })
      );
      console.error("Save error:", error);
    }
  };

  const onDownloadConfig = () => {
    const config = generateConfig(fields, markerMapping);
    try {
      downloadJson(config);
      message.success(
        intl.formatMessage({
          id: "app.settings.forms.field_configuration_saved",
        })
      );
    } catch (error) {
      message.error(
        intl.formatMessage({ id: "app.settings.forms.save_failed" })
      );
      console.error("Save error:", error);
    }
  };

  /**
   * Downloads configuration as JSON file
   * @param {Object} data - The configuration to download
   */
  const downloadJson = (data) => {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pdf-field-config.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(
        intl.formatMessage({ id: "app.settings.forms.configuration_copied" })
      );
    } catch (err) {
      message.error(
        intl.formatMessage({
          id: "app.settings.forms.configuration_copy_failed",
        })
      );
      console.error("Copy failed:", err);
    }
  };

  const handleDeleteField = (fieldId) => {
    setFields(fields.filter((f) => f.id !== fieldId));
    message.success(
      intl.formatMessage({ id: "app.settings.forms.field_deleted" })
    );
  };

  const handleDragStart = (e, field, value = null) => {
    e.stopPropagation();
    setDraggedField({ ...field, value });
  };

  const handleDrag = (e, field, value = null) => {
    e.stopPropagation();
    if (!draggedField) return;

    const canvas = document.querySelector(".react-pdf__Page canvas");
    if (!canvas) return;

    const coords = calculatePDFCoordinates(e, canvas);

    if (field.type === "checkbox" && value) {
      setMarkerMapping((prev) => ({
        ...prev,
        [field.name]: {
          ...prev[field.name],
          [value]: coords,
        },
      }));
    } else if (field.type === "list" && typeof value === "number") {
      setMarkerMapping((prev) => {
        const mappingKey = `${field.id}_${field.name}`;
        const mapping = prev[mappingKey] || {};
        const coordsArray = mapping[field.subFieldName] || [];
        const newCoordsArray = [...coordsArray];
        newCoordsArray[value] = coords;

        return {
          ...prev,
          [mappingKey]: {
            ...mapping,
            [field.subFieldName]: newCoordsArray,
          },
        };
      });
    } else {
      setFields(
        fields.map((f) => (f.id === field.id ? { ...f, ...coords } : f))
      );
    }
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    setDraggedField(null);
  };

  /**
   * Renders field markers on the current page
   * @returns {React.ReactNode[]} Array of field marker components
   */
  const renderFieldMarkers = () => {
    const canvas = canvasRef.current;
    const currentFields = fields.filter((f) => f.page === currentPage - 1);

    return currentFields.map((field) => {
      if (field.type === "checkbox") {
        const mapping = markerMapping[field.name] || {};
        return Object.entries(mapping).map(([value, coords]) => (
          <FieldMarker
            key={`${field.id}_${value}`}
            position={calculateDisplayPosition(
              coords.x,
              coords.y,
              canvas,
              pdfDimensions
            )}
            field={field.name}
            entity={field.entity}
            value={value}
            color="red"
            onDragStart={(e) => handleDragStart(e, field, value)}
            onDrag={(e) => handleDrag(e, field, value)}
            onDragEnd={handleDragEnd}
            onDelete={() => handleDeleteField(field.id)}
          />
        ));
      }

      if (field.type === "list") {
        const mappingKey = `${field.id}_${field.name}`;
        const mapping = markerMapping[mappingKey] || {};
        const coords = Array.isArray(mapping[field.subFieldName])
          ? mapping[field.subFieldName]
          : [];

        return coords
          .map((coord, index) => {
            if (!coord) return null; // Skip if coordinate is undefined

            return (
              <FieldMarker
                key={`${field.id}_${index}`}
                position={calculateDisplayPosition(
                  coord.x,
                  coord.y,
                  canvas,
                  pdfDimensions
                )}
                field={field.name}
                entity={field.entity}
                value={`${field.subFieldName}_${index}`}
                color="red"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, field, index)}
                onDrag={(e) => handleDrag(e, field, index)}
                onDragEnd={handleDragEnd}
                onDelete={() => handleDeleteField(field.id)}
              />
            );
          })
          .filter(Boolean); // Remove null entries
      }

      // Regular field marker rendering...
      return (
        <FieldMarker
          key={field.id}
          position={calculateDisplayPosition(
            field.x,
            field.y,
            canvas,
            pdfDimensions
          )}
          field={field.name}
          entity={field.entity}
          value={field.value}
          value_type={field.value_type}
          color="blue"
          onDragStart={(e) => handleDragStart(e, field)}
          onDrag={(e) => handleDrag(e, field)}
          onDragEnd={handleDragEnd}
          onDelete={() => handleDeleteField(field.id)}
        />
      );
    });
  };

  const content = (
    <Card>
      <Space direction="vertical" style={{ width: "100%" }}>
        {!pdfFile && !base64Content ? (
          <FileUploadSection
            onPDFUpload={handleFileChange}
            onConfigUpload={handleConfigFileUpload}
            intl={intl}
          />
        ) : (
          <>
            <ToolbarActions
              onAddField={startAddingField}
              onUndo={() => setFields(fields.slice(0, -1))}
              onSave={saveConfiguration}
              onShowConfig={() => {
                setCurrentConfig(generateConfig(fields, markerMapping));
                setShowConfigModal(true);
              }}
              onDownloadConfig={onDownloadConfig}
              currentPage={currentPage}
              totalPages={numPages}
              onPageChange={handlePageChange}
              hasFields={fields.length > 0}
              intl={intl}
              isFormContext={isFormContext}
            />

            <div
              ref={canvasRef}
              style={{
                position: "relative",
                cursor: isAddingField ? "crosshair" : "default",
                border: "1px solid #d9d9d9",
                borderRadius: "2px",
                overflow: "auto",
                maxHeight: "70vh",
                backgroundColor: "gray",
              }}
              onClick={handleFieldPlacement}
            >
              <PDFViewer
                pdfFile={pdfFile}
                currentPage={currentPage}
                scale={scale}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                onPageLoadSuccess={handlePageLoadSuccess}
              >
                {showMarkers && renderFieldMarkers()}
              </PDFViewer>
            </div>
          </>
        )}
      </Space>
    </Card>
  );

  // Render differently based on standalone mode
  return (
    <>
      {standalone ? (
        <>
          <Button
            icon={<FormOutlined />}
            onClick={() => setVisible(true)}
            type="primary"
          >
            {buttonText}
          </Button>
          <Modal
            title={intl.formatMessage({
              id: "app.settings.forms.pdf_field_mapper",
            })}
            open={visible}
            onCancel={() => setVisible(false)}
            width={1200}
            footer={null}
          >
            {content}
          </Modal>
        </>
      ) : (
        content
      )}

      <FieldConfigurationModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onFinish={handleFieldNameSubmit}
        intl={intl}
      />

      <ConfigPreviewModal
        visible={showConfigModal}
        onCancel={() => setShowConfigModal(false)}
        config={currentConfig}
        onCopy={() => copyToClipboard(JSON.stringify(currentConfig, null, 2))}
      />
    </>
  );
};

export default PDFFieldMapper;
